import React, { useRef } from 'react';
import { PaddingType } from '../../../puck/reusable-props/padding';
import { CustomizeProps } from '../../../interface/commonInterface';
import { getIsEdit } from '../../../utils/constants';
import { IBorderValueProps } from '../../../puck/reusable-props/Border';
import { getBorderProps, getPaddingStyles } from '../../../utils/helper';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import {
  setCheckoutErrorMessage,
  setIsTermsAccepted,
} from '../../../redux/slices/commonSlice';
import { useFormContext } from 'react-hook-form';
import { setCartData, setIsCartLoading } from '../../../redux/slices/cartSlice';
import checkoutService from '../../../api/checkoutService';
import {
  ShippingMethod,
  UpSellData,
} from '../../../interface/checkoutInterface';
import { setAvailableShippingMethods } from '../../../redux/slices/checkoutSlice';
import { FormData } from '../../../../page-builder/interface/checkoutInterface';

interface PayNowButtonProps extends ReduxDispatchProps, ReduxSelectorProps {
  showVip: boolean;
  isButton?: boolean;
  isAutoselect?: boolean;
  Button: CustomizeProps;
  setTermsError?: React.Dispatch<React.SetStateAction<boolean>>;
  ButtonText: CustomizeProps;
  currentDeviceType: string;
  styles: {
    backgroundColor: string;
    hoverColor: string;
    border: IBorderValueProps;
    width: string;
    padding: PaddingType;
  };
}

export const PayNowButton: React.FC<PayNowButtonProps> = ({
  showVip,
  styles,
  Button,
  ButtonText,
  setTermsError,
  currentDeviceType,
  isButton,
  isAutoselect = false,
  useAppDispatch,
  useAppSelector,
}) => {
  const isEditPage = getIsEdit();
  const dispatch = useAppDispatch();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { trigger } = useFormContext<FormData>();
  const { cartData } = useAppSelector(state => state.cart);
  const storeData = useAppSelector(state => state.store.storeData);
  const isCartLoading = useAppSelector(state => state.cart.isCartLoading);
  const { isSetShippingApiLoading, pageId } = useAppSelector(
    state => state.checkout,
  );
  const addProduct = useAppSelector(
    state => state.common.variantIdToAddIsTermsAccepted,
  );
  const isCartApiLoading = useAppSelector(state => state.cart.isCartApiLoading);

  const handleAdd = async () => {
    try {
      if (addProduct) {
        const isProductInCart = cartData?.LineItems?.some(
          (item: { VariantID: string | number }) =>
            String(item.VariantID) === addProduct,
        );
        if (!isProductInCart) {
          dispatch(setIsCartLoading(true));
          if (
            !cartData?.ID ||
            !storeData?.ShopID ||
            !storeData?.StoreID ||
            !addProduct
          ) {
            dispatch(setIsCartLoading(false));
            return;
          }
          const lineItems = [
            {
              Quantity: 1,
              VariantID: addProduct,
            },
          ];

          const payload = {
            ID: cartData?.ID,
            LineItems: lineItems,
            ShopID: storeData?.ShopID,
            StoreID: Number(storeData?.StoreID),
            PageID: String(pageId) || '',
          };

          const res = await checkoutService.checkoutAddItem(payload, {
            preUpsellData: {} as UpSellData,
            cartData,
          });
          if (res?.data?.Cart) {
            dispatch(setCartData(res.data.Cart));
            dispatch(
              setAvailableShippingMethods(
                res.data.Cart.AvailableShippingMethods as ShippingMethod[],
              ),
            );
          }
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        dispatch(setCheckoutErrorMessage(error.message));
      }
    } finally {
      dispatch(setIsCartLoading(false));
    }
  };

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isAutoselect) {
      e.preventDefault();
      dispatch(setIsTermsAccepted(true));
      await handleAdd();
      const isValid = await trigger();
      if (isValid) {
        if (buttonRef.current && !isButton) {
          buttonRef.current.click();
        }
      }
    }
    if (setTermsError) {
      setTermsError(true);
      trigger();
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type={isButton || isAutoselect ? 'button' : 'submit'}
        onClick={handleClick}
        style={{
          backgroundColor: styles?.backgroundColor,
          ...getBorderProps(styles.border, currentDeviceType),
          ...getPaddingStyles(styles.padding, currentDeviceType),
          transition: 'background-color 0.3s',
        }}
        disabled={
          isCartLoading || isSetShippingApiLoading > 0 || isCartApiLoading
        }
        onMouseOver={e => {
          e.currentTarget.style.backgroundColor = styles?.hoverColor;
        }}
        onMouseOut={e => {
          e.currentTarget.style.backgroundColor = styles?.backgroundColor;
        }}
        className={`${styles.width} bg-page_builder_theme ${styles.border?.borderStyle?.[currentDeviceType]} border-transparent  ${Button.fontSize?.[currentDeviceType]} ${Button.weight?.[currentDeviceType]} text-white shadow-sm sm:order-last`}>
        <div
          className="items-center justify-center flex"
          style={{
            color: Button.textColor?.[currentDeviceType],
            transition: 'background-color 0.3s',
          }}
          onMouseOver={e => {
            if (Button?.hoverColor) {
              e.currentTarget.style.color =
                Button.hoverColor?.[currentDeviceType];
            }
          }}
          onMouseOut={e => {
            e.currentTarget.style.color = Button.textColor?.[currentDeviceType];
          }}
          dangerouslySetInnerHTML={{ __html: Button?.text }}></div>
        {(showVip || isEditPage) && ButtonText.text && (
          <p
            className={`${ButtonText.fontSize?.[currentDeviceType]} ${ButtonText.weight?.[currentDeviceType]} my-2 `}
            style={{
              color: ButtonText.textColor?.[currentDeviceType],
              transition: 'background-color 0.3s',
            }}
            onMouseOver={e => {
              if (ButtonText?.hoverColor) {
                e.currentTarget.style.color =
                  ButtonText.hoverColor?.[currentDeviceType];
              }
            }}
            onMouseOut={e => {
              e.currentTarget.style.color =
                ButtonText.textColor?.[currentDeviceType];
            }}
            dangerouslySetInnerHTML={{ __html: ButtonText?.text }}></p>
        )}
      </button>
    </>
  );
};
